import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { SpinnerService } from '../spinner';
import { Observable, of } from 'rxjs';
import { HttpHeaderConstants } from '../http-header.constant';
import { catchError, finalize, retry } from 'rxjs/operators';
import { TfsDataMartField, TfsDataMartTable } from '../../models/tfs/tfs-table.model';
import { TfsPicklistEntry } from '../../models/tfs/tfs-picklist.model';
import { TfsCmtEntry } from '../../models/tfs/tfs-cmt.model';
import { TfsMergeField } from '../../models/tfs/tfs-merge-field.model';
import { TfsFormattedQuery } from '../../models/tfs/tfs-formatted-query.model';
import { PlayBuilderQueryCountResult } from '../../../lib/models/appcloud/play-builder-query-count-result.model';

@Injectable({
    providedIn: 'root',
})
export class TfsDataService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);
    readonly #errorHandlingService = inject(ErrorHandlingService);

    private TFS_FIELD_MERGE_TABLES_URL: string = environment.publicApiHost + '/tfs/metadata/fieldmergetables';
    private BASE_TFS_URL: string = environment.publicApiHost + '/tfs/metadata';
    private WEB_SEARCH_URL: string = environment.publicApiHost + '/tfs/metadata/websearch';
    private FORMATTED_QUERIES_URL: string = environment.publicApiHost + '/tfs/metadata/formatted-queries';
    private QUERY_COUNT_URL: string = environment.publicApiHost + '/tfs/metadata/query/count';
    private SEGMENTATION_QUERY_URL: string = environment.publicApiHost + '/tfs/metadata/query/segmentation';

    getDatamodel(
        installId: number,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<TfsDataMartTable[]> {
        const spinnerProcessName = 'TfsDataService.getDatamodel';
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving data model from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', installId.toString()).set('installDataId', installDataId),
        };

        return this.#http.get<TfsDataMartTable[]>(`${this.BASE_TFS_URL}/datamodel`, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve data model from TFS Data Mart. Please try again later.'
                );
                return of<TfsDataMartTable[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    /**
     * @description Accounts for missing display names fields or undefined values.
     * This isn't just a precaution, as it is also a reaction to real datamart data which had caused a ticket already for the null value case.
     * The issues caused downstream from the lack of ensuring data integrity causes multiple fields to not be computed, effectively making the reccomendations page non interactable.
     * Everything downstream of the TFS Datamodel Table data that this helps normalize in the Recommendations Component is a candidate for later enhancements.
     */
    public ensureDatamodelDataIntegrity(tfsDatamodel: TfsDataMartTable[]): TfsDataMartTable[] {
        tfsDatamodel.forEach(table => {
            if (!table.hasOwnProperty('displayName')) {
                table.displayName = 'UNKNOWN';
            }

            if (!table.displayName) {
                table.displayName = 'UNKNOWN';
            }
        });
        return tfsDatamodel;
    }

    getTables(
        appinstanceId: number,
        criteriaType: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<TfsDataMartTable[]> {
        const spinnerProcessName = 'TfsDataService.getGenericCriteriaTables.' + criteriaType;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving tables from Data Mart');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('criteriaType', criteriaType)
                .set('installDataId', installDataId),
        };

        return this.#http.get<TfsDataMartTable[]>(`${this.BASE_TFS_URL}/tables`, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve tables from TFS Data Mart. Please try again later.'
                );
                return of<TfsDataMartTable[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getTableFieldNames(
        appinstanceId: number,
        tableName: string,
        picklistType?: string,
        installDataId?: string,
        additionalHeaders: any = {}
    ): Observable<TfsDataMartField[]> {
        const spinnerProcessName = `TfsDataService.getTableFieldNames.${tableName}.${picklistType}`;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving table field names from Data Mart...');

        const params = {};
        params['appinstanceId'] = appinstanceId;
        picklistType ? (params['picklistType'] = picklistType) : null;
        installDataId ? (params['installDataId'] = installDataId) : null;

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams({ fromObject: params }),
        };

        return this.#http.get<TfsDataMartField[]>(this.buildTableFieldNamesUrl(tableName), customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve table field names from TFS Data Mart. Please try again later.'
                );
                return of<TfsDataMartField[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getMergeFieldTables(
        installId: number,
        installDataId?: string,
        additionalHeaders: any = {}
    ): Observable<TfsDataMartTable[]> {
        const spinnerProcessName = 'TfsDataService.getMergeFieldTables';
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving tables from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', installId.toString()).set('installDataId', installDataId),
        };

        return this.#http.get<TfsDataMartTable[]>(this.TFS_FIELD_MERGE_TABLES_URL, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve tables from TFS Data Mart. Please try again later.'
                );
                return of<TfsDataMartTable[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getMergeFields(
        installId: number,
        tableName: string,
        installDataId?: string,
        additionalHeaders: any = {}
    ): Observable<TfsMergeField[]> {
        const spinnerProcessName = 'TfsDataService.getMergeFields.' + tableName;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving merge fields from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', installId.toString()).set('installDataId', installDataId),
        };

        return this.#http.get<TfsMergeField[]>(this.buildMergeFieldsUrl(tableName), customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve fields from TFS Data Mart. Please try again later.'
                );
                return of<TfsMergeField[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getPicklistEntries(
        appinstanceId: number,
        tableName: string,
        fieldName: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<TfsPicklistEntry[]> {
        const spinnerProcessName = 'TfsDataService.getPicklistEntries.' + tableName + '.' + fieldName;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving picklist values from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('appinstanceId', appinstanceId.toString()).set('installDataId', installDataId),
        };

        return this.#http
            .get<TfsPicklistEntry[]>(this.buildPicklistValuesUrl(tableName, fieldName), customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to retrieve picklist field values from TFS Data Mart. Please try again later.'
                    );
                    return of<TfsPicklistEntry[]>();
                }),
                finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
            );
    }

    searchCmtCategories(
        appinstanceId: number,
        cmtLevel: number,
        searchTerm: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<TfsCmtEntry[]> {
        const spinnerProcessName = 'TfsDataService.searchCmtCategories.' + cmtLevel + '.' + searchTerm;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving CMT categories from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('searchTerm', searchTerm)
                .set('installDataId', installDataId),
        };

        return this.#http.get<TfsCmtEntry[]>(this.buildSearchCmtCategoriesUrl(cmtLevel), customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve CMT categories from TFS Data Mart. Please try again later.'
                );
                return of<TfsCmtEntry[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getTypeaheadValues(
        appinstanceId: number,
        tableName: string,
        fieldName: string,
        searchTerm: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<string[]> {
        const spinnerProcessName =
            'TfsDataService.getTypeaheadValues.' + tableName + '.' + fieldName + '.' + searchTerm;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving typeahead values from Data Mart...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('searchTerm', searchTerm)
                .set('installDataId', installDataId),
        };
        return this.#http.get<string[]>(this.buildTypeaheadSearchUrl(tableName, fieldName), customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve typeahead values from TFS Data Mart. Please try again later.'
                );
                return of<string[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getWebSearchValues(
        appinstanceId: number,
        searchTerm: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<string[]> {
        const spinnerProcessName = 'TfsDataService.getWebSearchValues.' + searchTerm;
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving web search values from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('searchTerm', searchTerm)
                .set('installDataId', installDataId),
        };
        return this.#http.get<string[]>(this.WEB_SEARCH_URL, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve web search values from TFS Data Mart. Please try again later.'
                );
                return of<string[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getFormattedQueries(
        appinstanceId: number,
        configData: string,
        modelType = '',
        installDataId: string,
        additionalHeaders: any = {},
        customErrorMessage?: string
    ): Observable<TfsFormattedQuery> {
        const spinnerProcessName = 'TfsDataService.getFormattedQueries';
        this.#spinnerService.addProcess(spinnerProcessName, 'Retrieving formatted queries from Data Mart...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('modelType', modelType)
                .set('installDataId', installDataId),
        };
        return this.#http.post<TfsFormattedQuery>(this.FORMATTED_QUERIES_URL, configData, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    customErrorMessage ||
                        'An error occurred while trying to retrieve formatted queries from TFS Data Mart. Please try again later.'
                );
                return of<TfsFormattedQuery>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    getSegmentationQuery(
        appinstanceId: number,
        configData: string,
        modelType = '',
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<string> {
        const spinnerProcessName = 'TfsDataService.getSegmentationQuery';
        this.#spinnerService.addProcess(spinnerProcessName, 'Getting segmentation query...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('modelType', modelType)
                .set('installDataId', installDataId),
        };

        return this.#http
            .post(this.SEGMENTATION_QUERY_URL, configData, { responseType: 'text', ...customHttpOptions })
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while to retrieve segmentation query from Tfs Data Mart. Please try again later.'
                    );
                    return of<string>();
                }),
                finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
            );
    }

    getQueryCount(
        appinstanceId: number,
        configData: string,
        modelType = '',
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<PlayBuilderQueryCountResult> {
        const spinnerProcessName = 'TfsDataService.getQueryCount';
        this.#spinnerService.addProcess(spinnerProcessName, 'Getting query result count...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('appinstanceId', appinstanceId.toString())
                .set('modelType', modelType)
                .set('installDataId', installDataId),
        };

        return this.#http.post<PlayBuilderQueryCountResult>(this.QUERY_COUNT_URL, configData, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while to retrieve query result/counts from Tfs Data Mart. Please try again later.'
                );
                return of<PlayBuilderQueryCountResult>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcessName))
        );
    }

    private buildMergeFieldsUrl(tableName: string): string {
        return this.TFS_FIELD_MERGE_TABLES_URL + '/' + tableName + '/fieldmerges';
    }

    private buildTableFieldNamesUrl(tableName: string): string {
        return this.BASE_TFS_URL + '/tables/' + tableName + '/fields';
    }

    private buildPicklistValuesUrl(tableName: string, fieldName: string): string {
        return this.BASE_TFS_URL + '/tables/' + tableName + '/fields/' + fieldName + '/picklist';
    }

    private buildSearchCmtCategoriesUrl(cmtLevel: number): string {
        return this.BASE_TFS_URL + '/cmts/' + cmtLevel + '/categories';
    }

    private buildTypeaheadSearchUrl(tableName: string, fieldName: string) {
        return this.BASE_TFS_URL + '/tables/' + tableName + '/fields/' + fieldName + '/typeahead';
    }
}
